/**
 * Portal Application Vuex store file
 * Controls data for the Portal
 * @module store
 * @see admin
 * @author Scott Gingerysty (Fresche Solutions)
 * @date May 2020
 */

import Vue from "vue";
import Vuex, { createStore } from "vuex";
import VuexPersistence from "vuex-persist";

import portalapi from "@services/portalapi";
import { getField, updateField } from "vuex-map-fields";

// Going outside the admin project folder to get the shared user store module
// @ is aliased to the src folder
import User from "@services/user-store";
import Carts from "./modules/Carts";
import WebUsers from "./modules/WebUsers";
import ClientPharmacy from "./modules/ClientPharmacy";
import Catalogs from "./modules/Catalogs";
import NarcoticControlForm from "./modules/NarcoticControlForm";
import LabelItems from "./modules/LabelItems";
import Invoices from "./modules/Invoices";
import SavedItems from "./modules/SavedItems";
import Orders from "./modules/Orders";
import ItemSearch from "./modules/ItemSearch";
import Items from "./modules/Items";
import Notifications from "./modules/Notifications";
import Reports from "./modules/Reports";
import Resources from "./modules/Resources";
import Statements from "./modules/Statements";
import Srps from "./modules/Srps";

const state = {
  isLoading: false,
  useLoading: false,
  loadingCount: 0,
};

const getters = {
  getField, // for vuex-map-fields
};

const mutations = {
  updateField, // for vuex-map-fields
  set(state, [variable, value]) {
    state[variable] = value;
  },

  // Loading indicator - on/off
  enableLoadingIndicator(state) {
    state.useLoading = true;
  },
  disableLoadingIndicator(state) {
    state.useLoading = false;
  },
  resetLoadingCount(state) {
    state.loadingCount = 0;
  },
};

const actions = {
  // Clear session storage data
  // PLEASE NOTE: All VueX modules MUST have a clearStateData mutation that gets called here
  async clearSessionStorageData({ commit }) {
    commit("Carts/clearStateData", null, { root: true });
    commit("Catalogs/clearStateData", null, { root: true });
    commit("ClientPharmacy/clearStateData", null, { root: true });
    commit("Invoices/clearStateData", null, { root: true });
    commit("Items/clearStateData", null, { root: true });
    commit("ItemSearch/clearStateData", null, { root: true });
    commit("LabelItems/clearStateData", null, { root: true });
    commit("NarcoticControlForm/clearStateData", null, { root: true });
    commit("Notifications/clearStateData", null, { root: true });
    commit("Orders/clearStateData", null, { root: true });
    commit("Reports/clearStateData", null, { root: true });
    commit("Resources/clearStateData", null, { root: true });
    commit("SavedItems/clearStateData", null, { root: true });
    commit("Srps/clearStateData", null, { root: true });
    commit("Statements/clearStateData", null, { root: true });
    commit("User/clearStateData", null, { root: true });
    commit("WebUsers/clearStateData", null, { root: true });
  },
};

const vuexLocal = new VuexPersistence({
  storage: window.sessionStorage,
  reducer: (state) => ({
    User: state.User,
    Carts: state.Carts,
  }),
});

let portalStore = createStore({
  namespaced: true,
  state,
  modules: {
    // PLEASE NOTE: All VueX modules MUST have a clearStateData mutation that gets called in the clearSessionStorageData action
    User,
    Carts,
    WebUsers,
    ClientPharmacy,
    Catalogs,
    NarcoticControlForm,
    LabelItems,
    ItemSearch,
    Invoices,
    SavedItems,
    Orders,
    Items,
    Notifications,
    Resources,
    Statements,
    Reports,
    Srps,
  },
  mutations,
  actions,
  getters,
  plugins: [vuexLocal.plugin]
});

export default portalStore;
